import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  FlaskConical, Plus, Trash2, Pencil, ChevronRight, ChevronLeft,
  BarChart3, Eye, MousePointerClick, CreditCard, Percent, Play, Pause,
  TrendingUp, AlertTriangle, Scale, Trophy, Info,
} from "lucide-react";

const STATUS_COLORS = {
  draft: "bg-[#F5F5F5] text-[#888888]",
  active: "bg-emerald-50 text-emerald-600 border-emerald-200",
  paused: "bg-amber-50 text-amber-600 border-amber-200",
  completed: "bg-blue-50 text-blue-600 border-blue-200",
};

export function AdminExperiments() {
  const [experiments, setExperiments] = useState([]);
  const [models, setModels] = useState([]);
  const [selected, setSelected] = useState(null);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchExperiments = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/experiments");
      setExperiments(data.experiments || []);
    } catch {
      toast.error("Failed to load experiments");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchModels = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/pricing/models");
      setModels(data.models || []);
    } catch { /* ignore */ }
  }, []);

  const fetchResults = useCallback(async (eid) => {
    try {
      const { data } = await api.get(`/admin/experiments/${eid}/results`);
      setResults(data);
    } catch {
      toast.error("Failed to load results");
    }
  }, []);

  useEffect(() => {
    fetchExperiments();
    fetchModels();
  }, [fetchExperiments, fetchModels]);

  useEffect(() => {
    if (selected) fetchResults(selected.experiment_id);
    else setResults(null);
  }, [selected, fetchResults]);

  const handleSave = async (formData) => {
    try {
      if (editing?.experiment_id) {
        await api.put(`/admin/experiments/${editing.experiment_id}`, formData);
        toast.success("Experiment updated");
      } else {
        await api.post("/admin/experiments", formData);
        toast.success("Experiment created");
      }
      setDialog(false);
      setEditing(null);
      fetchExperiments();
      if (selected) fetchResults(selected.experiment_id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    }
  };

  const handleDelete = async (eid) => {
    try {
      await api.delete(`/admin/experiments/${eid}`);
      toast.success("Experiment deleted");
      if (selected?.experiment_id === eid) setSelected(null);
      fetchExperiments();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const handleStatusToggle = async (exp) => {
    const newStatus = exp.status === "active" ? "paused" : "active";
    try {
      await api.put(`/admin/experiments/${exp.experiment_id}`, { status: newStatus });
      toast.success(newStatus === "active" ? "Experiment activated" : "Experiment paused");
      fetchExperiments();
      if (selected?.experiment_id === exp.experiment_id) {
        setSelected({ ...exp, status: newStatus });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update status");
    }
  };

  const handleApplyWinner = async (experimentId, winner) => {
    try {
      const { data } = await api.post(`/admin/experiments/${experimentId}/apply-winner`, { winner });
      toast.success(data.message);
      fetchExperiments();
      if (selected) {
        setSelected({ ...selected, status: "completed" });
        fetchResults(selected.experiment_id);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to apply winner");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // ── Detail View ──
  if (selected && results) {
    const r = results.results;
    const totalViews = r.A.views + r.B.views;
    const totalClicks = r.A.clicks + r.B.clicks;
    const totalConversions = r.A.conversions + r.B.conversions;

    return (
      <div className="space-y-4" data-testid="experiment-detail">
        <div className="flex items-center justify-between">
          <button
            onClick={() => { setSelected(null); setResults(null); }}
            className="flex items-center gap-1 text-sm text-[#888888] hover:text-[#111111] transition-colors"
            data-testid="experiment-back-button"
          >
            <ChevronLeft size={16} /> All Experiments
          </button>
          <div className="flex items-center gap-2">
            {selected.status !== "completed" && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleStatusToggle(selected)}
                data-testid="experiment-toggle-status"
              >
                {selected.status === "active" ? <><Pause size={13} className="mr-1" /> Pause</> : <><Play size={13} className="mr-1" /> Activate</>}
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setEditing(selected); setDialog(true); }}
              data-testid="experiment-edit-button"
            >
              <Pencil size={13} className="mr-1" /> Edit
            </Button>
          </div>
        </div>

        {/* Experiment Info */}
        <div className="bg-[#F5F5F5] rounded-lg p-4 border border-[#E5E5E5]">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-base font-medium text-[#111111]" data-testid="experiment-name">{selected.name}</h3>
            <Badge className={STATUS_COLORS[selected.status]} variant="outline">{selected.status}</Badge>
          </div>
          <div className="flex gap-4 text-xs text-[#888888] flex-wrap">
            <span>Split: <strong>{selected.traffic_split}% / {100 - selected.traffic_split}%</strong></span>
            <span>A: <code className="font-mono">{selected.model_key_a}</code></span>
            <span>B: <code className="font-mono">{selected.model_key_b}</code></span>
          </div>
        </div>

        {/* Results Summary */}
        <div className="grid grid-cols-4 gap-3" data-testid="experiment-summary-stats">
          <StatCard icon={Eye} label="Total Views" value={totalViews} color="bg-blue-50 text-blue-600" />
          <StatCard icon={MousePointerClick} label="Total Clicks" value={totalClicks} color="bg-amber-50 text-amber-600" />
          <StatCard icon={CreditCard} label="Conversions" value={totalConversions} color="bg-green-50 text-green-600" />
          <StatCard icon={Percent} label="Avg. Rate" value={totalViews > 0 ? `${((totalConversions / totalViews) * 100).toFixed(1)}%` : "—"} color="bg-purple-50 text-purple-600" />
        </div>

        <Separator />

        {/* Variant Comparison */}
        <h4 className="text-sm font-medium text-[#555555]">Variant Comparison</h4>
        <div className="grid grid-cols-2 gap-4" data-testid="experiment-variant-comparison">
          <VariantCard
            variant="A"
            modelKey={selected.model_key_a}
            split={selected.traffic_split}
            data={r.A}
          />
          <VariantCard
            variant="B"
            modelKey={selected.model_key_b}
            split={100 - selected.traffic_split}
            data={r.B}
          />
        </div>

        {/* Statistical Analysis */}
        {results.significance && (
          <SignificancePanel
            significance={results.significance}
            modelKeyA={selected.model_key_a}
            modelKeyB={selected.model_key_b}
            experimentId={selected.experiment_id}
            experimentStatus={selected.status}
            onApplyWinner={handleApplyWinner}
          />
        )}

        <ExperimentDialog
          open={dialog}
          onOpenChange={setDialog}
          experiment={editing}
          models={models}
          onSave={handleSave}
        />
      </div>
    );
  }

  // ── List View ──
  return (
    <div className="space-y-4" data-testid="experiments-list">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-medium text-[#111111]">A/B Experiments</h3>
          <p className="text-xs text-[#888888] mt-0.5">{experiments.length} experiment{experiments.length !== 1 ? "s" : ""}</p>
        </div>
        <Button
          size="sm"
          className="bg-blue-600 hover:bg-blue-700 text-white"
          onClick={() => { setEditing(null); setDialog(true); }}
          data-testid="experiment-create-button"
        >
          <Plus size={14} className="mr-1" /> New Experiment
        </Button>
      </div>

      {experiments.length === 0 ? (
        <div className="text-center py-12 text-sm text-[#888888]" data-testid="experiments-empty">
          No experiments yet. Create your first A/B test.
        </div>
      ) : (
        <div className="space-y-2">
          {experiments.map((exp) => {
            const s = exp.stats || { A: { view: 0, click: 0, conversion: 0 }, B: { view: 0, click: 0, conversion: 0 } };
            const totalViews = s.A.view + s.B.view;
            const totalConversions = s.A.conversion + s.B.conversion;

            return (
              <div
                key={exp.experiment_id}
                className="flex items-center gap-3 p-3 rounded-lg border border-[#E5E5E5] bg-white hover:border-[#E5E5E5] transition-colors cursor-pointer"
                onClick={() => setSelected(exp)}
                data-testid={`experiment-${exp.experiment_id}`}
              >
                <div className="w-9 h-9 rounded-lg bg-[#F5F5F5] flex items-center justify-center shrink-0">
                  <FlaskConical size={16} className="text-[#888888]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-[#111111]">{exp.name}</span>
                    <Badge className={`${STATUS_COLORS[exp.status]} text-[10px]`} variant="outline">{exp.status}</Badge>
                  </div>
                  <div className="text-xs text-[#888888] mt-0.5">
                    <code className="font-mono">{exp.model_key_a}</code>
                    <span className="mx-1.5 text-[#555555]">vs</span>
                    <code className="font-mono">{exp.model_key_b}</code>
                    <span className="mx-2 text-[#555555]">|</span>
                    <span>{exp.traffic_split}/{100 - exp.traffic_split} split</span>
                    {totalViews > 0 && (
                      <>
                        <span className="mx-2 text-[#555555]">|</span>
                        <span>{totalViews} views, {totalConversions} conv.</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                  {exp.status !== "completed" && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-[#888888]"
                      onClick={() => handleStatusToggle(exp)}
                      data-testid={`experiment-toggle-${exp.experiment_id}`}
                    >
                      {exp.status === "active" ? <Pause size={13} /> : <Play size={13} />}
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-[#888888] hover:text-red-500"
                    onClick={() => handleDelete(exp.experiment_id)}
                    data-testid={`experiment-delete-${exp.experiment_id}`}
                  >
                    <Trash2 size={13} />
                  </Button>
                  <ChevronRight size={16} className="text-[#555555] ml-1" />
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ExperimentDialog
        open={dialog}
        onOpenChange={setDialog}
        experiment={editing}
        models={models}
        onSave={handleSave}
      />
    </div>
  );
}


// ─── Significance Panel ───

function SignificancePanel({ significance, modelKeyA, modelKeyB, experimentId, experimentStatus, onApplyWinner }) {
  const s = significance;
  const [applying, setApplying] = useState(false);

  const statusConfig = {
    significant: {
      icon: Trophy,
      color: "text-emerald-600",
      bg: "bg-emerald-50 border-emerald-200",
      label: s.winner === "A"
        ? `Winner: Variant A (${modelKeyA})`
        : `Winner: Variant B (${modelKeyB})`,
    },
    not_significant: {
      icon: Scale,
      color: "text-[#888888]",
      bg: "bg-[#F5F5F5] border-[#E5E5E5]",
      label: "No significant difference",
    },
    insufficient_data: {
      icon: AlertTriangle,
      color: "text-amber-600",
      bg: "bg-amber-50 border-amber-200",
      label: "Insufficient data",
    },
    no_variance: {
      icon: Scale,
      color: "text-[#888888]",
      bg: "bg-[#F5F5F5] border-[#E5E5E5]",
      label: "No variance in data",
    },
  };

  const cfg = statusConfig[s.status] || statusConfig.insufficient_data;
  const StatusIcon = cfg.icon;

  return (
    <div className="space-y-3" data-testid="significance-panel">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-[#555555]">Statistical Analysis</h4>
        <div className="group relative">
          <Info size={13} className="text-[#888888] cursor-help" />
          <div className="absolute right-0 top-5 w-64 p-2.5 rounded-lg bg-zinc-900 text-white text-[11px] leading-relaxed opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10 pointer-events-none">
            Based on a two-proportion z-test. Results may be unreliable for small sample sizes. Significance threshold: p &lt; 0.05.
          </div>
        </div>
      </div>

      {/* Status Badge */}
      <div className={`rounded-lg border p-4 ${cfg.bg}`} data-testid="significance-status">
        <div className="flex items-center gap-2.5 mb-3">
          <StatusIcon size={18} className={cfg.color} />
          <span className={`text-sm font-medium ${cfg.color}`} data-testid="significance-label">{cfg.label}</span>
        </div>

        {s.status === "insufficient_data" ? (
          <div className="space-y-2">
            <p className="text-xs text-amber-600">
              Minimum thresholds not met: requires 100+ views per variant and 5+ total conversions.
            </p>
            {s.estimated_remaining_views > 0 && (
              <p className="text-xs text-amber-600 font-medium" data-testid="significance-remaining">
                Estimated additional traffic needed: ~{s.estimated_remaining_views} views per variant
              </p>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="significance-metrics">
            <div>
              <p className="text-[10px] font-mono uppercase tracking-widest text-[#888888] mb-0.5">Confidence</p>
              <p className={`text-lg font-heading font-medium ${s.is_significant ? "text-emerald-600" : "text-[#555555]"}`} data-testid="significance-confidence">
                {s.confidence}%
              </p>
            </div>
            <div>
              <p className="text-[10px] font-mono uppercase tracking-widest text-[#888888] mb-0.5">Lift (B vs A)</p>
              <p className={`text-lg font-heading font-medium ${s.lift > 0 ? "text-emerald-600" : s.lift < 0 ? "text-red-600" : "text-[#888888]"}`} data-testid="significance-lift">
                {s.lift > 0 ? "+" : ""}{s.lift}%
              </p>
            </div>
            <div>
              <p className="text-[10px] font-mono uppercase tracking-widest text-[#888888] mb-0.5">P-value</p>
              <p className="text-lg font-heading font-medium text-[#555555]" data-testid="significance-pvalue">
                {s.p_value < 0.001 ? "<0.001" : s.p_value.toFixed(4)}
              </p>
            </div>
            <div>
              <p className="text-[10px] font-mono uppercase tracking-widest text-[#888888] mb-0.5">Z-score</p>
              <p className="text-lg font-heading font-medium text-[#555555]" data-testid="significance-zscore">
                {s.z_score.toFixed(2)}
              </p>
            </div>
          </div>
        )}

        {s.status !== "insufficient_data" && s.estimated_remaining_views > 0 && !s.is_significant && (
          <p className="text-[11px] text-[#888888] mt-3 flex items-center gap-1.5" data-testid="significance-remaining">
            <TrendingUp size={12} className="shrink-0" />
            Estimated ~{s.estimated_remaining_views.toLocaleString()} more views per variant needed for significance
          </p>
        )}

        {s.is_significant && s.recommended_sample_per_variant > 0 && (
          <p className="text-[11px] text-emerald-600 mt-3" data-testid="significance-sample-size">
            Recommended sample: {s.recommended_sample_per_variant.toLocaleString()} views per variant (current threshold met)
          </p>
        )}

        {/* Apply Winner Button */}
        {s.is_significant && s.winner && experimentStatus !== "completed" && (
          <div className="mt-4 pt-3 border-t border-[#E5E5E5]" data-testid="apply-winner-section">
            <Button
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white w-full"
              disabled={applying}
              data-testid="apply-winner-button"
              onClick={async () => {
                setApplying(true);
                await onApplyWinner(experimentId, s.winner);
                setApplying(false);
              }}
            >
              <Trophy size={14} className="mr-1.5" />
              {applying ? "Applying..." : `Apply Winner — Set ${s.winner === "A" ? modelKeyA : modelKeyB} as active pricing`}
            </Button>
            <p className="text-[10px] text-[#888888] mt-1.5 text-center">
              This will set the winning model as the active default and close the experiment.
            </p>
          </div>
        )}

        {experimentStatus === "completed" && (
          <div className="mt-4 pt-3 border-t border-[#E5E5E5]" data-testid="experiment-completed-badge">
            <p className="text-xs text-blue-600 font-medium text-center">
              Experiment completed — winner applied
            </p>
          </div>
        )}
      </div>
    </div>
  );
}


// ─── Variant Card ───

function VariantCard({ variant, modelKey, split, data }) {
  const borderColor = variant === "A" ? "border-blue-200" : "border-emerald-200";
  const textColor = variant === "A" ? "text-blue-600" : "text-emerald-600";
  const bgColor = variant === "A" ? "bg-blue-50" : "bg-emerald-50";

  return (
    <div className={`rounded-lg border ${borderColor} p-4 bg-white`} data-testid={`variant-card-${variant}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Badge className={`${bgColor} ${textColor} text-xs`}>Variant {variant}</Badge>
          <span className="text-xs text-[#888888]">{split}% traffic</span>
        </div>
      </div>
      <p className="text-xs font-mono text-[#888888] mb-3">{modelKey}</p>
      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-[#888888]">Views</span>
          <span className="font-mono font-medium text-[#111111]" data-testid={`variant-${variant}-views`}>{data.views}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-[#888888]">Clicks</span>
          <span className="font-mono font-medium text-[#111111]" data-testid={`variant-${variant}-clicks`}>{data.clicks}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-[#888888]">Conversions</span>
          <span className="font-mono font-medium text-[#111111]" data-testid={`variant-${variant}-conversions`}>{data.conversions}</span>
        </div>
        <Separator />
        <div className="flex justify-between text-sm">
          <span className="text-[#888888] font-medium">Conversion Rate</span>
          <span className={`font-mono font-medium ${textColor}`} data-testid={`variant-${variant}-rate`}>
            {data.conversion_rate}%
          </span>
        </div>
      </div>
    </div>
  );
}


// ─── Stat Card ───

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="p-3 rounded-lg border border-[#E5E5E5] bg-white">
      <div className={`w-7 h-7 rounded-md flex items-center justify-center mb-1.5 ${color}`}>
        <Icon size={14} />
      </div>
      <p className="text-xl font-heading font-medium text-[#111111]">{value}</p>
      <p className="text-[10px] font-mono uppercase tracking-widest text-[#888888] mt-0.5">{label}</p>
    </div>
  );
}


// ─── Experiment Dialog ───

function ExperimentDialog({ open, onOpenChange, experiment, models, onSave }) {
  const [form, setForm] = useState({
    name: "", status: "draft", traffic_split: 50,
    model_key_a: "", model_key_b: "", starts_at: "", ends_at: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (experiment) {
      setForm({
        name: experiment.name || "",
        status: experiment.status || "draft",
        traffic_split: experiment.traffic_split || 50,
        model_key_a: experiment.model_key_a || "",
        model_key_b: experiment.model_key_b || "",
        starts_at: experiment.starts_at?.slice(0, 16) || "",
        ends_at: experiment.ends_at?.slice(0, 16) || "",
      });
    } else {
      setForm({ name: "", status: "draft", traffic_split: 50, model_key_a: "", model_key_b: "", starts_at: "", ends_at: "" });
    }
  }, [experiment, open]);

  const handleSubmit = async () => {
    setSaving(true);
    const data = { ...form, traffic_split: Number(form.traffic_split) };
    if (data.starts_at) data.starts_at = new Date(data.starts_at).toISOString();
    else data.starts_at = null;
    if (data.ends_at) data.ends_at = new Date(data.ends_at).toISOString();
    else data.ends_at = null;
    await onSave(data);
    setSaving(false);
  };

  const activeModels = models.filter((m) => m.status !== "archived");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="experiment-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{experiment?.experiment_id ? "Edit" : "New"} Experiment</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label className="text-xs">Name</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Spring Pricing Test"
              className="h-9 text-sm"
              data-testid="experiment-name-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Model A (Control)</Label>
              <Select value={form.model_key_a} onValueChange={(v) => setForm({ ...form, model_key_a: v })}>
                <SelectTrigger className="h-9 text-sm" data-testid="experiment-model-a-select"><SelectValue placeholder="Select model A" /></SelectTrigger>
                <SelectContent>
                  {activeModels.map((m) => (
                    <SelectItem key={m.model_id} value={m.key}>{m.name} ({m.key})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Model B (Variant)</Label>
              <Select value={form.model_key_b} onValueChange={(v) => setForm({ ...form, model_key_b: v })}>
                <SelectTrigger className="h-9 text-sm" data-testid="experiment-model-b-select"><SelectValue placeholder="Select model B" /></SelectTrigger>
                <SelectContent>
                  {activeModels.map((m) => (
                    <SelectItem key={m.model_id} value={m.key}>{m.name} ({m.key})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Traffic Split (% to A)</Label>
              <Input
                type="number"
                min={1}
                max={99}
                value={form.traffic_split}
                onChange={(e) => setForm({ ...form, traffic_split: e.target.value })}
                className="h-9 text-sm"
                data-testid="experiment-split-input"
              />
              <p className="text-[10px] text-[#888888]">A: {form.traffic_split}% — B: {100 - Number(form.traffic_split || 50)}%</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="h-9 text-sm" data-testid="experiment-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="paused">Paused</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Starts at <span className="text-[#888888]">(optional)</span></Label>
              <Input type="datetime-local" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} className="h-9 text-sm" data-testid="experiment-starts-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Ends at <span className="text-[#888888]">(optional)</span></Label>
              <Input type="datetime-local" value={form.ends_at} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} className="h-9 text-sm" data-testid="experiment-ends-input" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={handleSubmit} disabled={saving} data-testid="experiment-save-button">
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
